/**
 * ErrorBoundary Component
 * Catches React errors in component tree and displays user-friendly error message
 * Provides graceful error handling for the application
 */

import { Component, ReactNode, ErrorInfo } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component
 * Catches JavaScript errors anywhere in the child component tree
 * Logs error details and displays a fallback UI
 * 
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error details to console
   */
  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error details:', errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  /**
   * Reload the page to recover from error
   */
  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg text-fg font-pixel p-4">
          <div className="max-w-2xl w-full bg-control border-4 border-accent p-8 rounded-lg">
            <h1 className="text-xl mb-4 text-accent">
              ⚠️ Oops! Something went wrong
            </h1>
            
            <p className="text-xs mb-6 leading-relaxed">
              The application encountered an unexpected error. 
              Don't worry, your work might be saved in your browser's storage.
            </p>

            <button
              onClick={this.handleReload}
              className="bg-accent text-bg px-6 py-3 text-xs rounded hover:bg-fg hover:text-bg transition-colors border-2 border-accent cursor-pointer"
            >
              🔄 Reload Application
            </button>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-xs">
                <summary className="cursor-pointer text-accent hover:underline mb-2">
                  Show Error Details (Development Only)
                </summary>
                <div className="bg-bg p-4 rounded border-2 border-border overflow-auto">
                  <p className="font-bold mb-2">Error:</p>
                  <pre className="whitespace-pre-wrap break-words mb-4">
                    {this.state.error.toString()}
                  </pre>
                  
                  {this.state.errorInfo && (
                    <>
                      <p className="font-bold mb-2">Component Stack:</p>
                      <pre className="whitespace-pre-wrap break-words text-[10px]">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
